import { useMemo } from 'react';
import { Heartbeat } from '@/hooks/useHeartbeats';
import { buildDeviceColorMap } from '@/components/viz/vizPalette';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

const DAYS = 365;

interface Props {
  beats: Heartbeat[] | null;
  missing: boolean;
}

/**
 * Uptime timeline: one row per light sculpture, one cell per day over the
 * last year. Cell opacity reflects how many heartbeats landed in that day.
 */
export function UptimeTimeline({ beats, missing }: Props) {
  const t = useT(visualizeContent).charts;

  const { rows, colors, maxPerDay, span } = useMemo(() => {
    const byDevice = new Map<string, Map<number, number>>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayIndex = (iso: string) =>
      Math.floor((today.getTime() - new Date(iso).setHours(0, 0, 0, 0)) / 864e5);

    let oldest = 0;
    (beats ?? []).forEach((b) => {
      const d = dayIndex(b.created_at);
      if (d < 0 || d >= DAYS) return;
      if (d > oldest) oldest = d;
      let m = byDevice.get(b.device_id);
      if (!m) byDevice.set(b.device_id, (m = new Map()));
      m.set(d, (m.get(d) ?? 0) + 1);
    });

    const ids = Array.from(byDevice.keys()).sort();
    let max = 1;
    byDevice.forEach((m) => m.forEach((v) => (max = Math.max(max, v))));

    // Adaptive window: show only the period that actually contains data
    // (minimum two weeks) so a few days of history stay clearly visible.
    const span = Math.min(DAYS, Math.max(14, oldest + 1));

    return {
      rows: ids.map((id) => ({ id, days: byDevice.get(id)! })),
      colors: buildDeviceColorMap(ids),
      maxPerDay: max,
      span,
    };
  }, [beats]);

  if (missing) {
    return (
      <div className="text-sm text-sitefg/50 leading-relaxed">
        {t.heartbeatMissing}
      </div>
    );
  }

  if (!beats) return <div className="text-sm text-sitefg/40">{t.heartbeatLoading}</div>;

  if (!rows.length) {
    return (
      <div className="text-sm text-sitefg/50">
        {t.heartbeatEmpty}
      </div>
    );
  }

  const width = 960;
  const gap = 2;
  const cell = Math.max(3, width / span - gap);
  const rowH = Math.max(14, Math.min(28, 220 / rows.length));

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${span * (cell + gap)} ${rows.length * rowH + 8}`}
        width="100%"
        height={rows.length * rowH + 8}
        preserveAspectRatio="none"
      >
        {rows.map((r, ri) => (
          <g key={r.id} transform={`translate(0, ${ri * rowH})`}>
            {Array.from({ length: span }).map((_, d) => {
              const count = r.days.get(span - 1 - d) ?? 0;
              return (
                <rect
                  key={d}
                  x={d * (cell + gap)}
                  y={2}
                  width={cell}
                  height={rowH - 4}
                  rx={1}
                  fill={count ? colors.get(r.id) ?? 'var(--viz-5)' : 'currentColor'}
                  opacity={count ? 0.35 + 0.65 * Math.min(1, count / maxPerDay) : 0.06}
                />
              );
            })}
          </g>
        ))}
      </svg>
      <div className="mt-4 flex justify-between text-[10px] font-mono tracking-wider text-sitefg/40">
        <span>{span} d</span>
        <span>{t.heartbeatFooter(rows.length, beats.length)}</span>
        <span>{t.today}</span>
      </div>
    </div>
  );
}
